import { ref, computed, type Ref } from "vue";
import {
  getRestaurantDateKey,
  isSameRestaurantDay,
  getRestaurantDayRangeUtc,
} from "@lemmonhq/blogic";
import { useTablesSelectedDayData } from "../data";
import { useReservationCounts } from "@app/vue/composables/useReservationCounts";

const dateKeyToLocalDate = (dateKey: string) => {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export function useTableReservationMode(restaurantTz: Ref<string>) {
  const getTodayDate = () =>
    dateKeyToLocalDate(getRestaurantDateKey(new Date(), restaurantTz.value));
  const selectedDate = ref<Date>(getTodayDate());
  const showReservationSidebar = ref(false);
  const reservationSidebarTab = ref<"confirmed" | "pending" | "seated">(
    "confirmed",
  );
  const tableReservationFilter = ref<{
    tableId: number;
    tableName: string;
  } | null>(null);

  const isSelectedToday = computed(() =>
    isSameRestaurantDay(selectedDate.value, new Date(), restaurantTz.value),
  );

  const {
    reservations: displayedReservations,
    pendingRequests,
    isLoadingReservations,
    refetchSelectedDay,
    invalidateTablesData,
  } = useTablesSelectedDayData({ selectedDate, restaurantTz });

  const dayRange = computed(() =>
    getRestaurantDayRangeUtc(selectedDate.value, restaurantTz.value),
  );

  const reservationCounts = useReservationCounts({
    confirmedDateFrom: computed(() => dayRange.value.date_from),
    confirmedDateTo: computed(() => dayRange.value.date_to),
    seatedDateFrom: computed(() => dayRange.value.date_from),
    seatedDateTo: computed(() => dayRange.value.date_to),
    pendingDateFrom: computed(() => dayRange.value.date_from),
    pendingDateTo: computed(() => dayRange.value.date_to),
  });

  const loadReservationsForDate = async () => {
    await Promise.all([refetchSelectedDay(), reservationCounts.refresh()]);
  };

  const refreshTablesReservations = async () => {
    await invalidateTablesData();
    await Promise.all([refetchSelectedDay(), reservationCounts.refresh()]);
  };

  const enterReservationMode = () => {
    loadReservationsForDate();
  };

  const openReservationList = (tab: "confirmed" | "pending" | "seated") => {
    tableReservationFilter.value = null;
    reservationSidebarTab.value = tab;
    showReservationSidebar.value = true;
  };

  const closeReservationSidebar = () => {
    showReservationSidebar.value = false;
    tableReservationFilter.value = null;
  };

  const exitReservationMode = () => {
    showReservationSidebar.value = false;
    selectedDate.value = getTodayDate();
  };

  return {
    selectedDate,
    isSelectedToday,
    showReservationSidebar,
    reservationSidebarTab,
    tableReservationFilter,
    displayedReservations,
    pendingRequests,
    isLoadingReservations,
    loadReservationsForDate,
    refreshTablesReservations,
    enterReservationMode,
    openReservationList,
    closeReservationSidebar,
    exitReservationMode,
    reservationCounts,
  };
}
